import { useCallback, useRef } from "react";
import { PropertyType } from "@/tailwind/tailwindUtils";
import { createArbitraryOption } from "@/utils/typographyAdjustments";
import { applyTailwindChangeToClasses } from "@/components/RightStyle/TailwindStylingPanel/utils/applyChanges";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { TempoElement } from "@/utils/tempoElement";
import _ from "lodash";

interface TypographyState {
  fontSize: number;
  fontWeight: number;
  letterSpacing: number;
  lineHeight: number;
}

interface TypographyShortcutsConfig {
  iframesRef?: React.RefObject<
    Record<string, { ref: any; width: number; height: number }>
  >;
  selectedElementKeyRef: React.RefObject<string | null>;
  multiSelectedElementKeysRef: React.RefObject<string[]>;
  isMountedRef: React.RefObject<boolean>;
}

export const useTypographyShortcuts = ({
  iframesRef,
  selectedElementKeyRef,
  multiSelectedElementKeysRef,
  isMountedRef,
}: TypographyShortcutsConfig) => {
  const accumulatedRef = useRef<TypographyState>({
    fontSize: 0,
    fontWeight: 0,
    letterSpacing: 0,
    lineHeight: 0,
  });

  const baseValuesRef = useRef<Map<string, TypographyState>>(new Map());
  const lastSelectionRef = useRef<string>("");

  const getCurrentRenderedValues = useCallback(
    (element: TempoElement): TypographyState => {
      const defaults = {
        fontSize: 16,
        fontWeight: 400,
        letterSpacing: 0,
        lineHeight: 1.2,
      };

      try {
        // Resolve iframe for the element's storyboard
        const iframeData = iframesRef?.current?.[element.storyboardId];
        const iframeDoc = iframeData?.ref?.getIframeDocument();
        if (!iframeDoc) return defaults;

        // Locate the element inside the iframe
        const domElement = iframeDoc.querySelector(
          `[data-tempo-id="${element.codebaseId}"]`,
        );
        if (!domElement) return defaults;

        // Parse values with default fallback
        const view = iframeDoc.defaultView;
        const computed = view?.getComputedStyle(domElement);
        if (!computed) return defaults;

        const fontSize = parseFloat(computed.fontSize) || defaults.fontSize;
        const fontWeight =
          parseFloat(computed.fontWeight) || defaults.fontWeight;
        const letterSpacing =
          parseFloat(computed.letterSpacing) || defaults.letterSpacing;
        const lineHeight =
          parseFloat(computed.lineHeight) || defaults.lineHeight;

        return { fontSize, fontWeight, letterSpacing, lineHeight };
      } catch (error) {
        console.warn("Failed to get computed values from DOM:", error);
      }

      return defaults;
    },
    [iframesRef],
  );

  const applyTypographyChange = useCallback(
    (
      element: TempoElement,
      propertyType: PropertyType,
      newValue: number,
      isInstant: boolean,
    ) => {
      const treeElement =
        useCodebaseStore.getState().treeElementLookup[element.codebaseId];
      if (!treeElement) return;

      const elementClasses = treeElement.removableClasses || [];
      const option = createArbitraryOption(propertyType, newValue);

      const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
        currentElementClasses: elementClasses,
        option,
        propertyType,
        variant: null,
      });

      const change = new UpdateClassesChange({
        codebaseIdToUpdateClass: element.codebaseId,
        oldClasses: elementClasses,
        newClasses,
        involvedClasses,
        isInstantChangeOnly: isInstant,
      });

      const manager = useDesignCommandManager.getState();
      isInstant
        ? manager.sendInstantChange(change)
        : manager.executeCommand(change);
    },
    [],
  );

  const debouncedExecute = useRef(
    _.debounce(() => {
      if (!isMountedRef.current) return;

      const accumulated = accumulatedRef.current;
      const baseValues = baseValuesRef.current;

      // Only execute if there are actual accumulated changes
      const hasChanges = Object.values(accumulated).some(Boolean);

      if (!hasChanges) return;

      // Get current selection to ensure we only apply to currently selected elements
      const selectedElementKey = selectedElementKeyRef.current;
      const multiSelectedElementKeys =
        multiSelectedElementKeysRef.current || [];
      const currentSelectedKeys = [
        ...(selectedElementKey ? [selectedElementKey] : []),
        ...multiSelectedElementKeys,
      ];
      const currentElementKeys = new Set(currentSelectedKeys);

      for (const [elementKey, baseState] of baseValues) {
        // Only apply to elements that are still selected
        if (!currentElementKeys.has(elementKey)) continue;

        const element = TempoElement.fromKey(elementKey);
        if (element.isEmpty() || !element.isKnownElement()) continue;

        // Apply final changes for each property that has accumulated changes
        if (accumulated.fontSize !== 0) {
          applyTypographyChange(
            element,
            PropertyType.FONT_SIZE,
            baseState.fontSize + accumulated.fontSize,
            false,
          );
        }
        if (accumulated.fontWeight !== 0) {
          applyTypographyChange(
            element,
            PropertyType.FONT_WEIGHT,
            baseState.fontWeight + accumulated.fontWeight,
            false,
          );
        }
        if (accumulated.letterSpacing !== 0) {
          applyTypographyChange(
            element,
            PropertyType.LETTER_SPACING,
            baseState.letterSpacing + accumulated.letterSpacing,
            false,
          );
        }
        if (accumulated.lineHeight !== 0) {
          applyTypographyChange(
            element,
            PropertyType.LINE_HEIGHT,
            baseState.lineHeight + accumulated.lineHeight,
            false,
          );
        }
      }

      // Update base values to reflect the final state for currently selected elements only
      for (const [elementKey, baseState] of baseValues) {
        if (!currentElementKeys.has(elementKey)) continue;

        const updatedState = {
          fontSize: baseState.fontSize + accumulated.fontSize,
          fontWeight: baseState.fontWeight + accumulated.fontWeight,
          letterSpacing: baseState.letterSpacing + accumulated.letterSpacing,
          lineHeight: baseState.lineHeight + accumulated.lineHeight,
        };
        baseValuesRef.current.set(elementKey, updatedState);
      }

      // Reset accumulated changes only
      accumulatedRef.current = {
        fontSize: 0,
        fontWeight: 0,
        letterSpacing: 0,
        lineHeight: 0,
      };
    }, 1000),
  );

  const adjustTypography = useCallback(
    (propertyType: PropertyType, delta: number) => {
      // Get current selection from refs
      const selectedElementKey = selectedElementKeyRef.current;
      const multiSelectedElementKeys =
        multiSelectedElementKeysRef.current || [];

      const currentSelectedKeys = [
        ...(selectedElementKey ? [selectedElementKey] : []),
        ...multiSelectedElementKeys,
      ];

      const currentSelection = [...currentSelectedKeys].sort().join(",");

      // Clear state if selection changed
      if (lastSelectionRef.current !== currentSelection) {
        baseValuesRef.current.clear();
        accumulatedRef.current = {
          fontSize: 0,
          fontWeight: 0,
          letterSpacing: 0,
          lineHeight: 0,
        };
        lastSelectionRef.current = currentSelection;
      }

      // Remove base values for elements no longer in selection
      const currentElementKeys = new Set(currentSelectedKeys);
      for (const [elementKey] of baseValuesRef.current) {
        if (!currentElementKeys.has(elementKey)) {
          baseValuesRef.current.delete(elementKey);
        }
      }

      // Capture base values only for new elements
      for (const elementKey of currentSelectedKeys) {
        if (baseValuesRef.current.has(elementKey)) continue;

        const element = TempoElement.fromKey(elementKey);
        if (element.isEmpty() || !element.isKnownElement()) continue;

        const currentValues = getCurrentRenderedValues(element);
        baseValuesRef.current.set(elementKey, { ...currentValues });
      }

      // Accumulate changes
      const propertyKey =
        propertyType === PropertyType.FONT_SIZE
          ? "fontSize"
          : propertyType === PropertyType.FONT_WEIGHT
            ? "fontWeight"
            : propertyType === PropertyType.LETTER_SPACING
              ? "letterSpacing"
              : "lineHeight";

      accumulatedRef.current[propertyKey] += delta;

      // Apply instant visual feedback
      for (const elementKey of currentSelectedKeys) {
        const element = TempoElement.fromKey(elementKey);
        if (element.isEmpty() || !element.isKnownElement()) continue;

        const baseState = baseValuesRef.current.get(elementKey);
        if (!baseState) continue;

        const newValue =
          baseState[propertyKey] + accumulatedRef.current[propertyKey];
        applyTypographyChange(element, propertyType, newValue, true);
      }

      debouncedExecute.current();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCurrentRenderedValues, applyTypographyChange],
  );

  // Cleanup function to reset state
  const cleanup = useCallback(() => {
    baseValuesRef.current.clear();
    accumulatedRef.current = {
      fontSize: 0,
      fontWeight: 0,
      letterSpacing: 0,
      lineHeight: 0,
    };
    lastSelectionRef.current = "";
    debouncedExecute.current.cancel();
  }, []);

  return { adjustTypography, cleanup };
};
